import { Iam as IamSdk2019 } from "@smash-sdk/iam/ejs/10-2019";
import { Iam as IamSdk2021 } from "@smash-sdk/iam/ejs/07-2021";
import { Domain as DomainSdk } from "@smash-sdk/domain/ejs/10-2019";
import { DomainStore } from "../models/Domain.interface";
import { DomainInput } from "../models/DomainInput.interface";
import { GenerateTeamRoleInput } from "../models/GenerateTeamRoleInput.interface";
import { roles } from "../../roles/accessManagement/config/roles";
import { TeamRole } from "../../roles/accessManagement/roles";
import { getDecodedToken } from "../../../shared/helpers/helpers";
import bluebird from "bluebird";
import { DeleteDomainInput } from "../models/DeleteDomainInput.interface";
import { listTeamRemoteRoles } from "../../auth/services/permission.service";


const listDomains = async (): Promise<{ domains: DomainStore[] }> => {
    try {
        const iam2019 = new IamSdk2019({})
        const response = await iam2019.listTeams();
        if (response.teams) {
            const { teams } = response;
            return { domains: teams }
        }
        return { domains: [] }
    } catch (error) {
        throw error
    }
}

const createDomain = async (domainInput: DomainInput): Promise<any> => {
    try {
        const domainSdk = new DomainSdk({});
        const { domain: createdDomain } = await domainSdk.createDomain(domainInput);
        const { team, region, domain: domainName } = createdDomain;
        const { account } = getDecodedToken();
        await generateTeamRoles({ account, team, region, domain: domainName });
        const iam2019 = new IamSdk2019({});
        const updatedTeam = await iam2019.updateTeam({ teamId: team, name: createdDomain.domain });
        if (updatedTeam.team) {
            const { team } = updatedTeam;
            return { domain: team }
        }
    } catch (error) {
        throw error
    }
}

const generateTeamRoles = async (generateTeamRolesInput: GenerateTeamRoleInput): Promise<void> => {
    try {
        const { account, team } = generateTeamRolesInput;
        const teamRole = new TeamRole(roles.teamBasicAccess, { account, teamId: team });
        const policies = teamRole.policies;
        const policiesUnique = [];
        const policiesToCreate = new Map(); // Map to store unique policy names
        for (const policy of policies) {
            if (!policiesToCreate.has(policy.name)) {
                policiesToCreate.set(policy.name, true);
                policiesUnique.push(policy);
            }
        }
        // TO FIX
        const iam2019 = new IamSdk2019({});
        const iam2021 = new IamSdk2021({});
        await bluebird.map(policiesUnique, async (policy: any) => await iam2019.createPolicy(policy), { concurrency: 10 });
        await iam2019.createRole(teamRole);
        await bluebird.map(teamRole.policies,
            async (policy) => await iam2019.attachRolePolicy({ roleId: teamRole.name, policyId: policy.name }))
        await iam2021.attachTeamRole({ teamId: team, roleId: teamRole.name });
        return;
    } catch (error) {
        throw error;
    }
}

const deleteTeamRoles = async ({ teamId }: { teamId: string }): Promise<void> => {
    try {
        const iam2019 = new IamSdk2019({});
        const iam2021 = new IamSdk2021({});
        const teamRoles = await listTeamRemoteRoles({ teamId });
        await bluebird.map(teamRoles, async (teamRole) => {
            await bluebird.map(teamRole.policies, async (policy) => {
                await iam2019.detachRolePolicy({ policyId: policy.name, roleId: teamRole.name });
                await iam2019.deletePolicy({ policyId: policy.name });
            });
            await iam2021.detachTeamRole({ teamId, roleId: teamRole.name });
            await iam2021.deleteRole({ roleId: teamRole.name });
        })
    } catch (error) {
        throw (error);
    }
}

const deleteDomain = async (domainInput: DeleteDomainInput): Promise<void> => {
    try {
        const iam2021 = new IamSdk2021({});
        const { users } = await iam2021.listTeamUsers({ teamId: domainInput.team });
        if (users.length) {
            throw new Error('Forbidden to delete domain, delete users attached to this domain first');
        }
        await deleteTeamRoles({ teamId: domainInput.team });
        const domainSdk = new DomainSdk({});
        await domainSdk.deleteDomain(domainInput);
    } catch (error) {
        throw error
    }
}

const domainService = {
    listDomains,
    createDomain,
    deleteDomain,
}

export default domainService;